/**
 * Reaction Kinetics drill bank.
 *
 * Covers the kinetics topics most commonly tested at HS / Pre-AP level:
 *   - Factors that affect reaction rate (concentration, temperature, surface area, catalyst)
 *   - Collision theory & activation energy
 *   - Rate laws and reaction order
 *   - Reading energy profile diagrams
 *   - Catalysts (what they do, what they don't)
 */

export type KinCategory =
  | "factors"
  | "collision"
  | "rate-law"
  | "energy-profile"
  | "catalyst";

export type KinDifficulty = "easy" | "medium" | "hard";

export const KIN_CATEGORY_LABELS: Record<KinCategory, string> = {
  factors: "Rate Factors",
  collision: "Collision Theory",
  "rate-law": "Rate Laws & Order",
  "energy-profile": "Energy Profiles",
  catalyst: "Catalysts",
};

export interface KinProblem {
  id: string;
  category: KinCategory;
  difficulty: KinDifficulty;
  prompt: string;
  formula?: string;
  options: string[];
  correct: number;
  insight: string;
}

export const KIN_PROBLEMS: KinProblem[] = [
  // ── factors ────────────────────────────────────────────────────────────
  {
    id: "fac-1",
    category: "factors",
    difficulty: "easy",
    prompt: "Which of the following will generally INCREASE the rate of a reaction?",
    options: [
      "Lowering the temperature",
      "Decreasing the concentration of reactants",
      "Grinding a solid into powder (increasing surface area)",
      "Adding an inhibitor",
    ],
    correct: 2,
    insight:
      "More surface area = more particles exposed = more collisions per second. The other three options slow reactions.",
  },
  {
    id: "fac-2",
    category: "factors",
    difficulty: "easy",
    prompt:
      "Doubling the concentration of a reactant typically:",
    options: [
      "Has no effect on rate",
      "Increases the rate (more frequent collisions)",
      "Decreases the rate",
      "Lowers the activation energy",
    ],
    correct: 1,
    insight:
      "More particles per unit volume → more collisions per second → faster rate. The exact factor depends on the order in that reactant.",
  },
  {
    id: "fac-3",
    category: "factors",
    difficulty: "medium",
    prompt:
      "A rough rule of thumb says reaction rate roughly doubles for every 10 °C rise. Why?",
    options: [
      "Higher T raises activation energy.",
      "Higher T means more molecules have enough energy to overcome Ea, AND collide more often.",
      "Higher T destroys the catalyst.",
      "Higher T converts reactants into products instantly.",
    ],
    correct: 1,
    insight:
      "Two effects: (1) the Maxwell–Boltzmann distribution shifts so more molecules exceed Ea; (2) molecules move faster → more collisions per second.",
  },
  {
    id: "fac-4",
    category: "factors",
    difficulty: "medium",
    prompt:
      "Which factor does NOT change the rate of a reaction between two aqueous ions?",
    options: [
      "Concentration of the ions",
      "Temperature",
      "Surface area of the beaker",
      "Presence of a catalyst",
    ],
    correct: 2,
    insight:
      "Surface area only matters for heterogeneous reactions (involving a solid). For two solutions, the beaker's geometry is irrelevant.",
  },

  // ── collision theory ───────────────────────────────────────────────────
  {
    id: "col-1",
    category: "collision",
    difficulty: "easy",
    prompt:
      "According to collision theory, for a reaction to occur, particles must collide with:",
    options: [
      "Sufficient energy AND correct orientation",
      "Sufficient energy only",
      "Correct orientation only",
      "Any energy at any angle",
    ],
    correct: 0,
    insight:
      "Both conditions are required. Most collisions are unproductive — only a small fraction have both enough energy (≥ Ea) and the right geometry.",
  },
  {
    id: "col-2",
    category: "collision",
    difficulty: "medium",
    prompt: "What is activation energy (Ea)?",
    options: [
      "The energy released by the reaction.",
      "The minimum energy a colliding pair must possess for the reaction to proceed.",
      "The total energy of the products.",
      "The bond energy of the strongest bond in the products.",
    ],
    correct: 1,
    insight:
      "Ea is the energy 'hill' between reactants and products. Bigger Ea → fewer successful collisions → slower reaction at a given T.",
  },
  {
    id: "col-3",
    category: "collision",
    difficulty: "hard",
    prompt:
      "The Arrhenius equation k = A·e^(−Ea/RT) tells us that as temperature increases:",
    formula: "k = A·exp(−Ea / RT)",
    options: [
      "k decreases exponentially.",
      "k increases exponentially.",
      "k stays constant.",
      "Ea increases linearly.",
    ],
    correct: 1,
    insight:
      "As T rises, the exponent (−Ea/RT) becomes less negative, so e^(−Ea/RT) grows — k increases exponentially with T.",
  },

  // ── rate laws ──────────────────────────────────────────────────────────
  {
    id: "rl-1",
    category: "rate-law",
    difficulty: "easy",
    prompt: "What does the rate law rate = k[A][B] tell you?",
    formula: "rate = k[A][B]",
    options: [
      "Reaction is zero order overall.",
      "Reaction is first order in A, first order in B, second order overall.",
      "Reaction is second order in A only.",
      "Reaction does not depend on concentration.",
    ],
    correct: 1,
    insight:
      "Order in each species = its exponent in the rate law. Overall order = sum of exponents (1 + 1 = 2 here).",
  },
  {
    id: "rl-2",
    category: "rate-law",
    difficulty: "medium",
    prompt:
      "For a reaction with rate = k[A]², if [A] is doubled, the rate will:",
    formula: "rate = k[A]²",
    options: [
      "Stay the same",
      "Double",
      "Quadruple (×4)",
      "Increase 8-fold",
    ],
    correct: 2,
    insight:
      "Second order: rate ∝ [A]². Doubling [A] gives 2² = 4× the rate.",
  },
  {
    id: "rl-3",
    category: "rate-law",
    difficulty: "medium",
    prompt:
      "A reaction is zero order in B. If you triple [B], what happens to the rate?",
    options: [
      "Rate triples.",
      "Rate increases 9-fold.",
      "Rate is unchanged.",
      "Rate decreases to one third.",
    ],
    correct: 2,
    insight:
      "Zero order: rate = k[B]⁰ = k. Concentration of B has no effect — common when B is in large excess.",
  },
  {
    id: "rl-4",
    category: "rate-law",
    difficulty: "hard",
    prompt:
      "For 2NO + O₂ → 2NO₂, doubling [NO] quadruples the rate; doubling [O₂] doubles the rate. What is the rate law?",
    formula: "2NO + O₂ → 2NO₂",
    options: [
      "rate = k[NO][O₂]",
      "rate = k[NO]²[O₂]",
      "rate = k[NO][O₂]²",
      "rate = k[NO]²[O₂]²",
    ],
    correct: 1,
    insight:
      "Doubling [NO] → 4× rate means order 2 in NO (2² = 4). Doubling [O₂] → 2× means order 1 in O₂. So rate = k[NO]²[O₂].",
  },
  {
    id: "rl-5",
    category: "rate-law",
    difficulty: "hard",
    prompt:
      "The rate constant k depends on which of the following?",
    options: [
      "Concentration of reactants",
      "Temperature and the activation energy (and presence of a catalyst)",
      "The volume of the reaction vessel",
      "The size of the time interval used to measure the rate",
    ],
    correct: 1,
    insight:
      "k is independent of concentration. It changes with T (Arrhenius) and is lowered effectively by adding a catalyst (which lowers Ea).",
  },

  // ── energy profile ─────────────────────────────────────────────────────
  {
    id: "ep-1",
    category: "energy-profile",
    difficulty: "easy",
    prompt:
      "On a reaction energy profile, the activation energy is shown as:",
    options: [
      "The vertical distance from products down to reactants.",
      "The vertical distance from reactants up to the peak (transition state).",
      "The horizontal length of the reaction coordinate.",
      "The area under the curve.",
    ],
    correct: 1,
    insight:
      "Ea is the 'hill' from reactant level up to the transition-state peak. ΔH is the difference between reactant and product levels.",
  },
  {
    id: "ep-2",
    category: "energy-profile",
    difficulty: "medium",
    prompt:
      "On an energy profile, the products sit BELOW the reactants. This reaction is:",
    options: [
      "Endothermic (absorbs heat)",
      "Exothermic (releases heat)",
      "At equilibrium",
      "Non-spontaneous",
    ],
    correct: 1,
    insight:
      "Lower products = energy released to surroundings = exothermic, ΔH < 0.",
  },
  {
    id: "ep-3",
    category: "energy-profile",
    difficulty: "hard",
    prompt:
      "Which arrow on an energy diagram represents the enthalpy change (ΔH)?",
    options: [
      "Reactants to peak",
      "Peak to products",
      "Reactants to products (vertical distance, with sign)",
      "Reactants to reaction-coordinate axis",
    ],
    correct: 2,
    insight:
      "ΔH = H(products) − H(reactants). Negative if products are lower (exo), positive if higher (endo).",
  },

  // ── catalyst ───────────────────────────────────────────────────────────
  {
    id: "cat-1",
    category: "catalyst",
    difficulty: "easy",
    prompt: "What does a catalyst do to a reaction?",
    options: [
      "Increases the equilibrium yield",
      "Lowers the activation energy by providing an alternative pathway",
      "Raises the activation energy",
      "Changes ΔH of the reaction",
    ],
    correct: 1,
    insight:
      "Catalysts give a new pathway with lower Ea — both forward and reverse rates increase equally. Equilibrium position is unchanged.",
  },
  {
    id: "cat-2",
    category: "catalyst",
    difficulty: "medium",
    prompt: "A catalyst is consumed in the overall reaction.",
    options: [
      "True — it is used up like a reactant.",
      "False — it is regenerated and not consumed overall.",
      "True only at high temperatures.",
      "False — only homogeneous catalysts are regenerated.",
    ],
    correct: 1,
    insight:
      "By definition, a catalyst is regenerated. It may participate in intermediate steps but emerges unchanged at the end.",
  },
  {
    id: "cat-3",
    category: "catalyst",
    difficulty: "medium",
    prompt: "Which of the following is NOT changed by adding a catalyst?",
    options: [
      "Rate of the forward reaction",
      "Rate of the reverse reaction",
      "ΔH of the reaction",
      "Activation energy",
    ],
    correct: 2,
    insight:
      "Catalysts change kinetics, not thermodynamics. ΔH (and ΔG, K) are determined by the reactants/products, not the path taken.",
  },
  {
    id: "cat-4",
    category: "catalyst",
    difficulty: "hard",
    prompt:
      "Enzymes are biological catalysts. Which statement about them is correct?",
    options: [
      "Enzymes raise activation energy to control speed.",
      "Enzymes change the equilibrium constant of a reaction.",
      "Enzymes provide an alternative pathway with lower Ea, often increasing rates by orders of magnitude.",
      "Enzymes are consumed stoichiometrically with the substrate.",
    ],
    correct: 2,
    insight:
      "Enzymes are highly specific catalysts. They can speed reactions by 10⁶–10¹² times by stabilising the transition state.",
  },
];